/**
 * Geometric excess return versus SPY (issue #82).
 *
 * Long-run optimal is wealth-relative, not arithmetic: (1+r_s)/(1+r_b) − 1.
 * That is the terminal value of P_s/P_b, and it is the last gap on a SPY path
 * rebased onto the stock. Arithmetic r_s − r_b overstates the 3Y gap and does
 * not match the overlay. Sort order is unchanged while closes stay positive.
 *
 * A derivation, not a stored fact — the same reason `returnVolRatio` and
 * `risk_score` are never columns. Missing/non-finite legs stay null (R2b).
 */
import { WINDOW_KEYS, type WindowKey } from '@meridian/contracts';

export type WindowReturnMap = Readonly<Record<WindowKey, number | null>>;

/** `last/first − 1` over positive closes — the §6.5b total-return formula. */
export function windowReturn(closes: readonly number[]): number | null {
  if (closes.length < 2) return null;
  const first = closes[0];
  const last = closes[closes.length - 1];
  if (first === undefined || last === undefined) return null;
  if (!(first > 0) || !(last > 0)) return null;
  const ret = last / first - 1;
  return Number.isFinite(ret) ? ret : null;
}

/** Pending/failed series stay null (R2b) — the four screens share this seam. */
export function seriesWindowReturn(
  series: { readonly bars: readonly number[] } | undefined,
): number | null {
  return series === undefined ? null : windowReturn(series.bars);
}

/**
 * Geometric excess. `1 + spy <= 0` is undefined (division by zero / negative
 * wealth). A total-loss stock (`1 + stock === 0`) is −100% excess, a real
 * observation, not a missing one.
 */
export function excessReturn(stock: number | null, spy: number | null): number | null {
  if (stock === null || spy === null) return null;
  if (!Number.isFinite(stock) || !Number.isFinite(spy)) return null;
  const wealthSpy = 1 + spy;
  const wealthStock = 1 + stock;
  if (!(wealthSpy > 0) || !(wealthStock >= 0)) return null;
  const out = wealthStock / wealthSpy - 1;
  return Number.isFinite(out) ? out : null;
}

export function excessReturnMap(stock: WindowReturnMap, spy: WindowReturnMap): WindowReturnMap {
  return Object.fromEntries(
    WINDOW_KEYS.map((key) => [key, excessReturn(stock[key], spy[key])]),
  ) as Record<WindowKey, number | null>;
}

interface OverlayBase {
  readonly stock: number;
  readonly spy: number;
  readonly on: string;
}

function spyCloseOn(
  sessions: readonly string[],
  bars: readonly number[],
): ReadonlyMap<string, number> {
  /* Local Map fill — same sanctioned loop as generate-series. */
  /* eslint-disable functional/no-loop-statements -- session→close index */
  const out = new Map<string, number>();
  for (const [index, on] of sessions.entries()) {
    const close = bars[index];
    if (close !== undefined && close > 0 && Number.isFinite(close)) out.set(on, close);
  }
  return out;
  /* eslint-enable functional/no-loop-statements */
}

function firstAlignedBase(
  stockBars: readonly number[],
  stockSessions: readonly string[],
  spyBySession: ReadonlyMap<string, number>,
): OverlayBase | null {
  /* eslint-disable functional/no-loop-statements -- first-match scan */
  for (const [index, on] of stockSessions.entries()) {
    const stock = stockBars[index];
    const spy = spyBySession.get(on);
    if (stock !== undefined && stock > 0 && Number.isFinite(stock) && spy !== undefined) {
      return { stock, spy, on };
    }
  }
  return null;
  /* eslint-enable functional/no-loop-statements */
}

/**
 * SPY closes in the stock's price units, aligned on market dates.
 *
 * `stock[base] * spy[t] / spy[base]` — the last stock/overlay ratio is
 * `excessReturn(windowReturn(stock), windowReturn(spy))`. A date the stock
 * traded and SPY did not is a hole, never an interpolated print (R2b).
 */
export function spyIndexedOverlay(
  stockBars: readonly number[],
  stockSessions: readonly string[],
  spyBars: readonly number[],
  spySessions: readonly string[],
): readonly (number | null)[] {
  const empty = stockBars.map(() => null);
  if (stockBars.length !== stockSessions.length) return empty;
  if (spyBars.length !== spySessions.length) return empty;
  const spyBySession = spyCloseOn(spySessions, spyBars);
  const base = firstAlignedBase(stockBars, stockSessions, spyBySession);
  if (base === null) return empty;
  return stockSessions.map((on) => {
    const spy = spyBySession.get(on);
    if (spy === undefined) return null;
    const indexed = base.stock * (spy / base.spy);
    return Number.isFinite(indexed) ? indexed : null;
  });
}

/**
 * SPY closes on the stock's own calendar, from the overlay base forward — the
 * exact support `spyIndexedOverlay` draws its non-null entries from. Deriving
 * both from one session walk is what makes "the cell matches the overlay" a
 * property of the code rather than of two functions agreeing by coincidence.
 */
function alignedSpyCloses(
  stockSessions: readonly string[],
  spyBySession: ReadonlyMap<string, number>,
  from: string,
): readonly number[] {
  const start = stockSessions.indexOf(from);
  if (start < 0) return [];
  return stockSessions.slice(start).flatMap((on) => {
    const spy = spyBySession.get(on);
    return spy === undefined ? [] : [spy];
  });
}

/**
 * SPY last/first over the sessions the STOCK traded, from the overlay base —
 * the stock-screen denominator, so the VS SPY cell matches the last overlay
 * gap. Full-window `windowReturn(spy)` remains the ranking denominator.
 *
 * The end bound is the stock's last shared session, not the end of the SPY
 * series (#161). SPY trades on after a halt, a missing last bar, or a chart
 * window longer than the stock's tape; the overlay never sees those sessions,
 * so a denominator that did made the cell disagree with the chart. One shared
 * session is a zero-length benchmark window and stays null (R2b) — pairing it
 * with a multi-session stock return would invent alpha out of the calendar.
 */
export function alignedSpyWindowReturn(
  stockBars: readonly number[],
  stockSessions: readonly string[],
  spyBars: readonly number[],
  spySessions: readonly string[],
): number | null {
  if (stockBars.length !== stockSessions.length) return null;
  if (spyBars.length !== spySessions.length) return null;
  const spyBySession = spyCloseOn(spySessions, spyBars);
  const base = firstAlignedBase(stockBars, stockSessions, spyBySession);
  if (base === null) return null;
  return windowReturn(alignedSpyCloses(stockSessions, spyBySession, base.on));
}
